import {
  isModulePreloaded,
  TilePacketFiles,
  TilePacketMissingDependencies,
  getDeprecatedModule,
} from '@apptile/tile-packet-sdk';
import mapValues from 'lodash/mapValues';

import type { FileDependencies } from '@/ast/readers/dependencies/parse';
import { NOTIFICATION_UNBUNDLEABLE } from '@/ast/writers/onesignal/edit';
import {
  SDKVersion,
  TilePacketCodeFile,
  TilePacketDependencies,
  Annotation,
  AnnotationAction,
} from '@/shared/types/tile-packet.types';
import { getAbsolutePath } from '@/shared/utils/path/path';

const LOCAL_FILES = ['.js', '.ts', '.tsx', '/index.js', '/index.ts', '/index.tsx'];

export function getPackageJsonFromDependencies(
  dependencies: TilePacketDependencies,
): TilePacketCodeFile {
  return {
    type: 'CODE',
    contents: JSON.stringify(
      { dependencies: mapValues(dependencies, (dep) => dep.version) },
      null,
      2,
    ),
  };
}

// Modules the run-engine PREVIEW provides itself as no-ops (see tile-run-engine
// aliases/common.tsx). tile-push is a native OTA SDK: OTA is meaningless in the
// preview, and its real native bundle can't be built for the web preview (the
// bundler hangs on it). So the preview must NOT ask the bundler to build these —
// the run-engine aliases them to a no-op instead. Real app BUILDS are
// unaffected: they npm-install the real SDK from package.json (which still
// lists these; only the PREVIEW's fetch list drops them).
const PREVIEW_PROVIDED_MODULES = new Set<string>([
  '@apptile/tile-push-react-native',
  // Runtime font loading for Brand text styles. Unlike everything else in this
  // set, the run-engine aliases the REAL implementation (see aliases/common.tsx)
  // rather than a no-op — live font swapping is the whole point of the Brand
  // font picker.
  //
  // The package is public on npm, so the bundler COULD build it. That is exactly
  // what must not happen: it would bundle its own expo-font and react alongside
  // the app's, and hooks running against a second React's dispatcher fail with
  // "Cannot read properties of null (reading 'useMemo')".
  '@tiledev/tile-fonts',
  '@hot-updater/react-native',
  '@hot-updater/core',
  'hot-updater',
  // OneSignal push: only the expo config plugin, which is build-time Node code
  // the bundler genuinely cannot build. The SDK and the native module it wraps
  // both bundle fine and must stay in the fetch list — see NOTIFICATION_UNBUNDLEABLE
  // in ./onesignalCodegen for why each one matters.
  ...NOTIFICATION_UNBUNDLEABLE,
]);

export function getPackageJsonDependencies(
  packageJson: TilePacketCodeFile,
  sdkVersion: SDKVersion,
): { [name: string]: string } | undefined {
  try {
    const { dependencies: jsonDeps } = JSON.parse(packageJson.contents);
    if (typeof jsonDeps !== 'object') return undefined;
    Object.keys(jsonDeps).forEach((name) => {
      if (isModulePreloaded(name, sdkVersion, true) || PREVIEW_PROVIDED_MODULES.has(name)) {
        delete jsonDeps[name];
      }
    });
    return jsonDeps;
  } catch {}
  return undefined;
}

/**
 * Build the package.json the editor holds/saves by MERGING the panel's dependency
 * set back into the REAL package.json file — instead of reconstructing a bare
 * `{ dependencies }` object (`getPackageJsonFromDependencies`), which drops every
 * other field (name/version/main/scripts/private/devDependencies/overrides) AND
 * every runtime-preloaded / preview-provided dep (expo/react/react-native/…,
 * tile-push). That bare reconstruction is what stripped package.json on save.
 *
 * Semantics:
 *   • preloaded + preview-provided deps → kept verbatim from the real file (the
 *     panel deliberately hides them, so they're absent from `dependencies` — we
 *     must NOT interpret that absence as a removal).
 *   • every other dep → driven by `dependencies` (the panel's authoritative set):
 *     version updates apply, panel removals drop, panel additions append.
 *   • all non-`dependencies` fields → preserved byte-for-byte via the parsed file.
 *
 * Falls back to the legacy bare object only when there's genuinely no real file to
 * preserve (missing / non-CODE / unparseable) so a brand-new/invalid app still works.
 */
export function mergeDependenciesIntoPackageJson(
  originalPkg: TilePacketCodeFile | undefined,
  dependencies: TilePacketDependencies,
  sdkVersion: SDKVersion,
): TilePacketCodeFile {
  if (!originalPkg || originalPkg.type !== 'CODE') {
    return getPackageJsonFromDependencies(dependencies);
  }
  let parsed: any;
  try {
    parsed = JSON.parse(originalPkg.contents);
  } catch {
    return getPackageJsonFromDependencies(dependencies);
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    return getPackageJsonFromDependencies(dependencies);
  }

  const origDeps: { [name: string]: string } =
    parsed.dependencies && typeof parsed.dependencies === 'object' ? parsed.dependencies : {};
  const nextDeps: { [name: string]: string } = {};

  // Walk the real file's deps in order: keep hidden (preloaded/preview) deps as-is;
  // for panel-managed deps, keep only if still present (version may have changed).
  for (const name of Object.keys(origDeps)) {
    if (isModulePreloaded(name, sdkVersion, true) || PREVIEW_PROVIDED_MODULES.has(name)) {
      nextDeps[name] = origDeps[name];
    } else if (dependencies[name]) {
      nextDeps[name] = dependencies[name].version;
    }
    // else: the panel removed a non-hidden dep → drop it.
  }
  // Append deps the panel ADDED that weren't in the real file yet.
  for (const name of Object.keys(dependencies)) {
    if (!(name in nextDeps)) nextDeps[name] = dependencies[name].version;
  }

  parsed.dependencies = nextDeps;
  return {
    type: 'CODE',
    // Preserve the file's trailing-newline convention. `JSON.stringify` never
    // emits one, and package.json on disk almost always ends with `\n`, so
    // without this the rebuild changes exactly one byte on an app whose
    // dependencies did not change at all. That flips the file's content hash,
    // which is what the publish flow diffs to decide whether a Cloud Push is
    // needed (`codeChangeReason`) — costing a ~5 minute push to ship a
    // newline. It self-corrects after that push lands (the stripped version
    // becomes the new baseline), so the damage is one spurious push per app
    // rather than one per publish — still worth not doing. Reading the
    // convention off the original rather than hardcoding `\n` keeps this
    // idempotent for a file that genuinely has no trailing newline.
    contents: JSON.stringify(parsed, null, 2) + (originalPkg.contents.endsWith('\n') ? '\n' : ''),
  };
}

function getPackageJsonLocation(
  name: string,
  lines: string[],
  version: boolean = false,
): {
  fileName: string;
  startLineNumber: number;
  endLineNumber: number;
  startColumn: number;
  endColumn: number;
} {
  if (name) {
    for (let line = 1; line <= lines.length; line++) {
      const col = lines[line - 1].indexOf(`"${name}"`);
      if (col >= 0) {
        return {
          fileName: 'package.json',
          startLineNumber: line,
          endLineNumber: line,
          startColumn: col + 1 + (version ? name.length + 4 : 0),
          endColumn: lines[line - 1].length + (lines[line - 1].endsWith(',') ? 0 : 1),
        };
      }
    }
  }
  return {
    fileName: 'package.json',
    startLineNumber: 1,
    endLineNumber: lines.length,
    startColumn: 1,
    endColumn: lines[lines.length - 1].length,
  };
}

export function getDependencyAnnotations(
  packageJson: TilePacketCodeFile,
  dependencies: TilePacketDependencies,
  missingDependencies: TilePacketMissingDependencies,
  files: TilePacketFiles,
  filesDependencies: { [path: string]: FileDependencies },
  sdkVersion: SDKVersion,
  getDependencyAction: (
    name: string,
    version: string,
    dependencies: TilePacketDependencies,
    sdkVersion: SDKVersion,
  ) => AnnotationAction,
): Annotation[] {
  const lines = packageJson.contents.split('\n');
  let json: any;
  try {
    json = JSON.parse(packageJson.contents);
  } catch {
    return [
      {
        message: 'Invalid JSON.',
        location: getPackageJsonLocation('', lines),
        severity: 4,
        source: 'Dependencies',
      },
    ];
  }

  // Verify that package.json passes the minimum valid requirements
  if (typeof json !== 'object' || typeof json.dependencies !== 'object') {
    return [
      {
        message: `Object 'dependencies' not found.`,
        location: getPackageJsonLocation('', lines),
        severity: 4,
        source: 'Dependencies',
      },
    ];
  }

  const annotations: Annotation[] = [];

  // Give warnings about unsupported package.json entries
  for (const key in json) {
    if (key !== 'dependencies') {
      annotations.push({
        message: `Key '${key}' is not supported.`,
        location: getPackageJsonLocation(key, lines),
        severity: 2,
        source: 'Dependencies',
      });
    }
  }

  // Add annotations for package.json
  for (const name in dependencies) {
    const { error, handle, version, wantedVersion } = dependencies[name];
    const deprecated = getDeprecatedModule(name, sdkVersion);
    if (error) {
      annotations.push({
        message: error.message,
        location: getPackageJsonLocation(name, lines, false),
        severity: 4,
        source: 'Dependencies',
        action: getDependencyAction(name, version, dependencies, sdkVersion),
      });
    } else if (!handle && !isModulePreloaded(name, sdkVersion)) {
      annotations.push({
        message: `Resolving '${name}@${version}' ...`,
        location: getPackageJsonLocation(name, lines, false),
        severity: -1,
        source: 'Dependencies',
      });
    }
    if (wantedVersion && wantedVersion !== version) {
      annotations.push({
        message: `'${name}@${version}' is not the recommended version for SDK ${sdkVersion}.`,
        location: getPackageJsonLocation(name, lines, false),
        severity: isModulePreloaded(name, sdkVersion, true) ? 2 : 3,
        source: 'Dependencies',
        action: getDependencyAction(name, version, dependencies, sdkVersion),
      });
    }
    if (deprecated) {
      annotations.push({
        message: `'${name}' is deprecated. ${deprecated}`,
        location: getPackageJsonLocation(name, lines, false),
        severity: 2,
        source: 'Dependencies',
      });
    }
  }

  // Add annotations for all missing (peer) dependencies
  for (const name in missingDependencies) {
    annotations.push({
      message: `'${missingDependencies[name].dependents.join(
        ',',
      )}' requires peer-dependency '${name}'.`,
      location: getPackageJsonLocation(missingDependencies[name].dependents[0], lines, false),
      severity: isModulePreloaded(name, sdkVersion, true)
        ? 2
        : isModulePreloaded(name, sdkVersion)
        ? 3
        : 4,
      source: 'Dependencies',
      action: getDependencyAction(
        name,
        missingDependencies[name].wantedVersion!,
        dependencies,
        sdkVersion,
      ),
    });
  }

  // Add annotations for all files that have missing dependencies
  for (const path in filesDependencies) {
    const fileDeps = filesDependencies[path];
    for (const name in fileDeps) {
      const fileDep = fileDeps[name];
      if (fileDep.isPackage && !isModulePreloaded(name, sdkVersion, true)) {
        const dep = dependencies[name];
        if (!dep) {
          const isPreloaded = isModulePreloaded(name, sdkVersion);
          annotations.push({
            message: `'${name}' is not defined in dependencies.`,
            location: fileDep.location,
            severity: isPreloaded ? 3 : 4,
            source: 'Dependencies',
            action: getDependencyAction(name, fileDep.version ?? '*', dependencies, sdkVersion),
          });
        }
      } else if (!fileDep.isPackage) {
        // @/ is a path alias whose root can vary by project (bare root or src/).
        // Leading slash in the resolved name makes getAbsolutePath treat it as
        // project-root-relative rather than relative to the current file.
        const candidates = name.startsWith('@/')
          ? ['/' + name.slice(2), '/src/' + name.slice(2)]
          : [name];
        const fileExists = candidates.some((candidate) => {
          const absPath = getAbsolutePath(candidate, path);
          return files[absPath] || LOCAL_FILES.some((suffix) => files[absPath + suffix]);
        });
        if (!fileExists) {
          annotations.push({
            message: `Cannot find file '${name}'.`,
            location: fileDep.location,
            severity: 4,
            source: 'Dependencies',
          });
        }
      }
    }
  }

  return annotations;
}
